import { useMutation, useQueryClient } from '@tanstack/react-query';
import { favoritesApi, isPlaceInFavorites } from '@/features/favorites/api/favoritesApi';
import { Place } from '@/types/place';

/**
 * Custom hook for managing place favorites with optimistic updates.
 * 
 * @param userId - User ID (can be null)
 * @param placeId - Place ID to check/manage
 * @param favorites - Optional cached favorites array. If provided, uses cached data instead of API call.
 */
export const usePlaceFavorite = (
  userId: string | null,
  placeId: string,
  favorites?: Place[]
) => {
  const queryClient = useQueryClient();

  // Check favorite status using cached data if available, otherwise fall back to query
  // This maintains backward compatibility during migration
  const isFavorite = favorites
    ? isPlaceInFavorites(placeId, favorites)
    : false; // If no favorites provided, component should handle loading state

  // Toggle favorite mutation with optimistic updates
  const toggleMutation = useMutation({
    mutationFn: async (newValue: boolean) => {
      if (newValue) {
        await favoritesApi.add(userId!, placeId);
      } else {
        await favoritesApi.remove(userId!, placeId);
      }
      return newValue;
    },
    // Optimistic update: immediately update UI
    onMutate: async (newValue: boolean) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['favorite', userId, placeId] });
      await queryClient.cancelQueries({ queryKey: ['userFavorites', userId] });

      // Snapshot previous value
      const previousValue = queryClient.getQueryData<boolean>(['favorite', userId, placeId]);

      // Optimistically update
      queryClient.setQueryData(['favorite', userId, placeId], newValue);

      // Return context for rollback
      return { previousValue };
    },
    // Rollback on error
    onError: (err, newValue, context) => {
      if (context?.previousValue !== undefined) {
        queryClient.setQueryData(['favorite', userId, placeId], context.previousValue);
      }
    },
    // Invalidate on success (refetch to ensure consistency)
    onSuccess: () => {
      // Invalidate userFavorites query to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['userFavorites', userId] });
      // Also invalidate individual favorite query for backward compatibility
      queryClient.invalidateQueries({ queryKey: ['favorite', userId, placeId] });
    },
  });

  return {
    isFavorite,
    isLoading: false, // No loading state when using cached data
    toggleFavorite: () => toggleMutation.mutate(!isFavorite),
    isToggling: toggleMutation.isPending,
    error: toggleMutation.error,
  };
};
